import React, { useState, useEffect, useCallback, useRef } from "react";
import axiosInstance from "../helpers/axiosInstance";
import View from '../images/view.svg';
import Edit from '../images/edit.png';
import Delete from "../images/delete.svg";
import Swal from "sweetalert2";
import { debounce } from 'lodash';
import { useNavigate } from "react-router-dom";
import { useLoading } from '../context/LoadingContext';
import * as XLSX from 'xlsx';
import './vendor.css';

const Vendor = () => {
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageInput, setPageInput] = useState("1");
    const [searchTerm, setSearchTerm] = useState("");
    const [editVendorId, setEditVendorId] = useState(null);
    const [updatedTimes, setUpdatedTimes] = useState({});
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newVendor, setNewVendor] = useState({
        vendor_name: "",
        vendor_code: "",
        early_entry_hour: "",
        late_arrival_hour: ""
    });
    const [file, setFile] = useState(null);
    const fileInputRef = useRef(null);
    const navigate = useNavigate();
    const { updateLoading } = useLoading();

    // Fetch vendors
    const fetchVendors = useCallback(async () => {
        try {
            updateLoading(true);
            setLoading(true);
            const response = await axiosInstance.get(`/api/vendor/vendors`, {
                params: { page, limit: 10, number: searchTerm },
            });
            setVendors(response.data.vendors);
            setTotalPages(response.data.totalPages);
            setError("");
        } catch (error) {
            console.error("Error fetching vendors:", error);
            setError("Failed to fetch vendors.");
        } finally {
            setLoading(false);
            updateLoading(false);
        }
    }, [page, searchTerm, updateLoading]);

    useEffect(() => {
        fetchVendors();
    }, [fetchVendors]);

    useEffect(() => {
        const debouncedFetch = debounce(() => {
            fetchVendors();
        }, 300);
        debouncedFetch();
        return () => debouncedFetch.cancel();
    }, [searchTerm, page, fetchVendors]);

    useEffect(() => {
        setPage(1);
        setPageInput("1");
    }, [searchTerm]);

    useEffect(() => {
        setPageInput(page.toString());
    }, [page, totalPages]);

    const handleView = (id) => {
        navigate(`/vendor/${id}`);
    };

    const syncVendors = async () => {
        try {
            const result = await Swal.fire({
                title: "Sync Vendors?",
                text: "This will fetch the latest vendor data. Do you want to continue?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, Sync Now!",
                cancelButtonText: "Cancel",
            });

            if (result.isConfirmed) {
                setLoading(true);
                Swal.fire({
                    title: "Syncing Vendors...",
                    text: "Please wait while we sync all vendors.",
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });
                const response = await axiosInstance.post("/api/vendor/vendors/sync");
                Swal.close();

                Swal.fire("Success!", response.data.message || "Vendors synced successfully!", "success");
                fetchVendors();

            }
        } catch (err) {
            console.error("Error syncing vendors:", err);

            const backendMessage =
                err.response?.data?.message ||
                err.response?.data?.error ||
                "Sync failed. Please try again.";

            Swal.fire("Error!", backendMessage, "error");
        }
        finally {
            setLoading(false);
        }
    };

    const handleEdit = (vendor) => {
        setEditVendorId(vendor.id);
        setUpdatedTimes({
            vendor_name: vendor.vendor_name || "",
            late_arrival_hour: vendor.VendorEntryPolicy?.late_arrival_hour || "",
            early_entry_hour: vendor.VendorEntryPolicy?.early_entry_hour || "",
        });
    };


    const handleInputChange = (e, field) => {
        let value = e.target.value;

        if (field !== "vendor_name") {
            // For numeric fields only
            if (value === "-" || value < 0) {
                value = "";
            }
        }

        setUpdatedTimes((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleNewVendorInputChange = (e, field) => {
        let value = e.target.value;

        if (field !== "vendor_name" && field !== "vendor_code") {
            // For numeric fields only
            if (value === "-" || value < 0) {
                value = "";
            }
        }

        setNewVendor((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const handlePageInputChange = (e) => {
        setPageInput(e.target.value);
    };

    const handlePageInputBlur = () => {
        const val = parseInt(pageInput, 10);
        if (!isNaN(val) && val >= 1 && val <= totalPages) {
            setPage(val);
        } else {
            setPageInput(page.toString());
        }
    };

    const handlePageInputKeyDown = (e) => {
        if (e.key === "Enter") {
            handlePageInputBlur();
        }
    };

    const handleUpdateVendor = async (id) => {
        try {
            const result = await Swal.fire({
                title: "Update Vendor?",
                text: "Do you want to save the updated entry policy?",
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Yes, Update",
                cancelButtonText: "Cancel",
            });

            if (result.isConfirmed) {
                await axiosInstance.put(`/api/vendor/vendors/${id}`, updatedTimes);
                fetchVendors();
                setEditVendorId(null);
                Swal.fire("Updated!", "Vendor entry policy updated successfully.", "success");
            }
        } catch (err) {
            console.error("Error updating vendor:", err);

            const backendMessage =
                err.response?.data?.message ||
                err.response?.data?.error ||
                "Failed to update vendor. Please try again.";

            Swal.fire("Error!", backendMessage, "error");
        }

    };

    const handleAddVendor = async () => {
        if (!newVendor.vendor_name || !newVendor.vendor_code) {
            Swal.fire("Error", "Vendor Name and Vendor Code are required!", "error");
            return;
        }

        try {
            const result = await Swal.fire({
                title: "Add Vendor?",
                text: "Do you want to add this vendor?",
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Yes, Add",
                cancelButtonText: "Cancel",
            });

            if (result.isConfirmed) {
                await axiosInstance.post(`/api/vendor/vendors/manual`, {
                    vendor_name: newVendor.vendor_name,
                    vendor_code: newVendor.vendor_code,
                    early_entry_hour: newVendor.early_entry_hour || null,
                    late_arrival_hour: newVendor.late_arrival_hour || null,
                });
                Swal.fire("Success!", "Vendor added successfully.", "success");
                setShowAddModal(false);
                setNewVendor({
                    vendor_name: "",
                    vendor_code: "",
                    early_entry_hour: "",
                    late_arrival_hour: ""
                });
                fetchVendors();
            }
        } catch (err) {
            console.error("Error adding vendor:", err);

            const backendMessage =
                err.response?.data?.message ||
                err.response?.data?.error ||
                "Failed to add vendor. Please try again.";

            Swal.fire("Error!", backendMessage, "error");
        }
    };

    // Export to Excel
    const handleExport = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/api/vendor/vendors`, {
                params: { limit: 10000, page: 1 }, // Fetch all without pagination
            });
            const allVendors = response.data.vendors;

            const exportData = allVendors.map((vendor) => ({
                'Vendor Name': vendor.vendor_name || '',
                'Vendor Code': vendor.vendor_code || '',
                'Early Entry Time (hr)': vendor.VendorEntryPolicy?.early_entry_hour ?? 'N/A',
                'Late Arrival Time (hr)': vendor.VendorEntryPolicy?.late_arrival_hour ?? 'N/A',
            }));

            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Vendors');
            XLSX.writeFile(wb, `vendors_${new Date().toISOString().split('T')[0]}.xlsx`);
            Swal.fire({
                icon: "success",
                title: "Export Started",
                text: "File Downloading has Started!",
                confirmButtonText: "OK",
            });
        } catch (err) {
            console.error("Error exporting vendors:", err);
            Swal.fire("Error!", "Failed to export vendors. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadFormat = () => {
        const dummyData = [
            { 'Vendor Name': "ABC Supplier", 'Vendor Code': "V001", 'Late Arrival Time': 2, 'Early Entry Time': 1 },
        ];
        const worksheet = XLSX.utils.json_to_sheet(dummyData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "VendorFormat");
        XLSX.writeFile(workbook, "Vendor_Format.xlsx");
    };

    const handleFileUploadClick = () => {
        fileInputRef.current.click();
    };

    // Handle file selection
    const handleFileChange = (e) => {
        const selected = e.target.files[0];
        if (!selected) return;
        setFile(selected);
        console.log("Selected file:", selected);
    };

    // Upload Excel file
    const handleUpload = async () => {
        if (!file) {
            Swal.fire("Error", "Please select a file before uploading!", "error");
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        try {
            setLoading(true);
            await axiosInstance.post("/api/vendor/upload", formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            Swal.fire("Success!", "Vendors uploaded successfully!", "success");
            setShowUploadModal(false);
            setFile(null);
            fileInputRef.current.value = null;
            fetchVendors();
        } catch (err) {
            console.error("Upload failed:", err);

            const backendMessage =
                err.response?.data?.error ||
                err.response?.data?.details ||
                "Failed to upload file. Please try again.";

            Swal.fire("Error!", backendMessage, "error");
        }
        finally {
            setLoading(false);
        }
    };

    return (
        <div className="container-fluid mt-3">
            <div className="responsive-scroll">
                <div className="row mt-3">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            {/* Search Input */}
                            <input
                                type="text"
                                className="form-control w-25 rounded-pill"
                                placeholder="Search by Name or Code..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ width: "250px" }}
                            />

                            {/* Buttons */}
                            <div>
                                <button
                                    onClick={() => setShowAddModal(true)}
                                    className="btn mx-2"
                                    style={{ backgroundColor: "rgb(10, 69, 149)", color: "white" }}
                                >
                                    <i className="bi bi-plus-lg me-1"></i> Add
                                </button>
                                <button
                                    onClick={() => setShowUploadModal(true)}
                                    className="btn mx-2"
                                    style={{ backgroundColor: "rgb(10, 69, 149)", color: "white" }}
                                >
                                    <i className="bi bi-people-fill me-1"></i> Add in Bulk
                                </button>
                                <button
                                    onClick={syncVendors}
                                    className="btn mx-2"
                                    style={{ backgroundColor: "#f59e0b", color: "white" }}
                                    disabled={loading}
                                >
                                    <i className="bi bi-arrow-repeat me-1"></i>
                                    {loading ? " Syncing..." : " Sync Now"}
                                </button>
                                <button
                                    onClick={handleExport}
                                    className="btn btn-success mx-2"
                                    disabled={loading}
                                >
                                    <i className="bi bi-upload me-1"></i> Export
                                </button>
                            </div>
                        </div>
                    </div>
                </div>


                {error && <p className="text-red-500">{error}</p>}

                {/* ---------- Add Single Vendor Modal ---------- */}
                {showAddModal && (
                    <>
                        <div
                            className="modal-backdrop fade show"
                            style={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                backgroundColor: "rgba(0, 0, 0, 0.5)",
                                zIndex: 1040
                            }}
                            onClick={() => setShowAddModal(false)}
                        />
                        <div className="modal show fade d-block" tabIndex="-1" role="dialog" style={{ zIndex: 1050 }}>
                            <div className="modal-dialog modal-dialog-centered" role="document">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title">Add New Vendor</h5>
                                        <button
                                            type="button"
                                            className="close"
                                            onClick={() => setShowAddModal(false)}
                                        >
                                            <span>&times;</span>
                                        </button>
                                    </div>
                                    <div className="modal-body">
                                        <div className="form-group">
                                            <label>Vendor Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={newVendor.vendor_name}
                                                onChange={(e) => handleNewVendorInputChange(e, "vendor_name")}
                                                placeholder="Enter vendor name"
                                            />
                                        </div>
                                        <div className="form-group mt-3">
                                            <label>Vendor Code</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={newVendor.vendor_code}
                                                onChange={(e) => handleNewVendorInputChange(e, "vendor_code")}
                                                placeholder="Enter vendor code"
                                            />
                                        </div>
                                        <div className="form-group mt-3">
                                            <label>Early Entry Hour (hr)</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={newVendor.early_entry_hour}
                                                onChange={(e) => handleNewVendorInputChange(e, "early_entry_hour")}
                                                min="0"
                                                onKeyDown={(e) => {
                                                    if (e.key === "-" || e.key === "e") e.preventDefault();
                                                }}
                                                placeholder="Enter early entry hours"
                                            />
                                        </div>
                                        <div className="form-group mt-3">
                                            <label>Late Arrival Hour (hr)</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={newVendor.late_arrival_hour}
                                                onChange={(e) => handleNewVendorInputChange(e, "late_arrival_hour")}
                                                min="0"
                                                onKeyDown={(e) => {
                                                    if (e.key === "-" || e.key === "e") e.preventDefault();
                                                }}
                                                placeholder="Enter late arrival hours"
                                            />
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn"
                                            style={{
                                                backgroundColor: "#6c757d",
                                                color: "white",
                                                border: "none",
                                                padding: "8px 16px",
                                                borderRadius: "4px",
                                                fontSize: "14px"
                                            }}
                                            onClick={() => setShowAddModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn"
                                            style={{
                                                backgroundColor: "rgb(10, 69, 149)",
                                                color: "white",
                                                border: "none",
                                                padding: "8px 16px",
                                                borderRadius: "4px",
                                                fontSize: "14px"
                                            }}
                                            onClick={handleAddVendor}
                                        >
                                            Add Vendor
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
                {/* ---------- End Add Single Vendor Modal ---------- */}

                {/* ---------- Upload Modal ---------- */}
                {showUploadModal && (
                    <>
                        <div
                            className="modal-backdrop fade show"
                            style={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                backgroundColor: "rgba(0, 0, 0, 0.5)",
                                zIndex: 1040
                            }}
                            onClick={() => setShowUploadModal(false)}
                        />
                        <div className="modal show fade d-block" tabIndex="-1" role="dialog" style={{ zIndex: 1050 }}>
                            <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title">Upload Vendor Excel</h5>
                                        <button
                                            type="button"
                                            className="close"
                                            onClick={() => setShowUploadModal(false)}
                                        >
                                            <span>&times;</span>
                                        </button>
                                    </div>
                                    <div className="modal-body">
                                        {/* Hidden file input */}
                                        <input
                                            type="file"
                                            accept=".xlsx, .xls, .csv"
                                            ref={fileInputRef}
                                            style={{ display: "none" }}
                                            onChange={handleFileChange}
                                        />

                                        {/* Custom upload box */}
                                        <div
                                            className="position-relative d-flex flex-column align-items-center justify-content-center p-4 mt-3"
                                            style={{
                                                border: "2px dashed #ccc",
                                                borderRadius: "10px",
                                                width: "100%",
                                                textAlign: "center",
                                                backgroundColor: "#fafafa",
                                                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                                                position: "relative",
                                            }}
                                        >
                                            {/* Top right Download Format button */}
                                            <button
                                                className="btn position-absolute"
                                                style={{
                                                    top: "15px",
                                                    right: "20px",
                                                    borderRadius: "20px",
                                                    fontWeight: "500",
                                                    backgroundColor: "rgb(10, 69, 149)",
                                                    color: "white",
                                                    border: "none",
                                                    padding: "6px 12px",
                                                    fontSize: "12px"
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDownloadFormat();
                                                }}
                                            >
                                                <i className="bi bi-download me-2"></i> Download Format
                                            </button>

                                            <p className="h5 mb-2 mt-4 fw-bold" style={{ color: "#0A4595", fontWeight: "600" }}>
                                                Select File Here
                                            </p>

                                            <p className="mb-4" style={{ color: "#6c757d", fontSize: "0.9rem" }}>
                                                If you want to see which data is required, please download the format above.
                                            </p>

                                            <div className="d-flex align-items-center" style={{ width: "50%", minWidth: "220px", maxWidth: "250px" }}>
                                                {/* File box */}
                                                <div
                                                    className="flex-grow-1 p-1"
                                                    style={{ backgroundColor: "#d8d8d8ff", cursor: "pointer", border: "1px solid #9e9e9eff" }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleFileUploadClick();
                                                    }}
                                                >
                                                    <span className="text-truncate">
                                                        Choose File
                                                    </span>
                                                </div>

                                                {/* Span outside box on the right */}
                                                <span
                                                    className="text-muted ms-2"
                                                    style={{ whiteSpace: "nowrap", fontSize: "0.9rem", alignSelf: "center" }}
                                                >
                                                    {file ? file.name : "No file chosen"}
                                                </span>
                                            </div>

                                            {/* File supported text with red star */}
                                            <p
                                                className="mt-2"
                                                style={{ color: "#6c757d", fontSize: "0.9rem", fontStyle: "italic" }}
                                            >
                                                <span style={{ color: "red" }}>*</span> Files Supported: CSV, XLS, XLSX
                                            </p>

                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn"
                                            style={{
                                                backgroundColor: "#6c757d",
                                                color: "white",
                                                border: "none",
                                                padding: "8px 16px",
                                                borderRadius: "4px",
                                                fontSize: "14px"
                                            }}
                                            onClick={() => setShowUploadModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn"
                                            style={{
                                                backgroundColor: "rgb(10, 69, 149)",
                                                color: "white",
                                                border: "none",
                                                padding: "8px 16px",
                                                borderRadius: "4px",
                                                fontSize: "14px"
                                            }}
                                            onClick={handleUpload}
                                            disabled={!file}
                                        >
                                            Upload
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
                {/* ---------- End Upload Modal ---------- */}

                {/* ---------- Existing Table ---------- */}
                <div className="table-responsive mt-4">
                    <table className="table table-sm">
                        <thead className="text-white">
                            <tr>
                                <th className="text-center text-white py-2 px-0" style={{ backgroundColor: "#0A4595" }}>Sl. No.</th>
                                <th className="text-center text-white py-2 px-0" style={{ backgroundColor: "#0A4595" }}>Vendor Name</th>
                                <th className="text-center text-white py-2 px-0" style={{ backgroundColor: "#0A4595" }}>Vendor Code</th>
                                <th className="text-center text-white py-2 px-0" style={{ backgroundColor: "#0A4595" }}>Early Arrival Time</th>
                                <th className="text-center text-white py-2 px-0" style={{ backgroundColor: "#0A4595" }}>Late Arrival Time</th>
                                <th className="text-center text-white py-2 px-0" style={{ backgroundColor: "#0A4595" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {vendors.length > 0 ? (
                                vendors.map((vendor, index) => (
                                    <tr key={vendor.id}>
                                        <td className="text-center p-2" style={{ minWidth: "80px" }}>
                                            {(page - 1) * 10 + index + 1}
                                        </td>

                                        <td className="p-2" style={{ minWidth: "200px", maxWidth: "300px" }}>
                                            {editVendorId === vendor.id ? (
                                                <input
                                                    type="text"
                                                    className="form-control text-left"
                                                    value={updatedTimes.vendor_name}
                                                    onChange={(e) => handleInputChange(e, "vendor_name")}
                                                    style={{ width: "100%" }}
                                                />
                                            ) : (
                                                <div
                                                    className="mx-auto text-left"
                                                    style={{
                                                        maxWidth: "300px",
                                                        wordWrap: "break-word",
                                                        whiteSpace: "normal"
                                                    }}
                                                    title={vendor.vendor_name} // shows full name on hover
                                                >
                                                    {vendor.vendor_name}
                                                </div>
                                            )}
                                        </td>

                                        <td className="text-center p-2" style={{ minWidth: "100px" }}>
                                            {vendor.vendor_code}
                                        </td>

                                        <td className="text-center p-2" style={{ minWidth: "120px" }}>
                                            {editVendorId === vendor.id ? (
                                                <div className="input-group mx-auto" style={{ maxWidth: "120px" }}>
                                                    <input
                                                        type="number"
                                                        className="form-control text-center"
                                                        value={updatedTimes.early_entry_hour}
                                                        onChange={(e) => handleInputChange(e, "early_entry_hour")}
                                                        min="0"
                                                        onKeyDown={(e) => {
                                                            if (e.key === "-" || e.key === "e") e.preventDefault();
                                                        }}
                                                    />
                                                    <span className="input-group-text">hr</span>
                                                </div>
                                            ) : (
                                                <>
                                                    {vendor.VendorEntryPolicy?.early_entry_hour ?? "N/A"} hr
                                                </>
                                            )}
                                        </td>

                                        <td className="text-center p-2" style={{ minWidth: "120px" }}>
                                            {editVendorId === vendor.id ? (
                                                <div className="input-group mx-auto" style={{ maxWidth: "120px" }}>
                                                    <input
                                                        type="number"
                                                        className="form-control text-center"
                                                        value={updatedTimes.late_arrival_hour}
                                                        onChange={(e) => handleInputChange(e, "late_arrival_hour")}
                                                        min="0"
                                                        onKeyDown={(e) => {
                                                            if (e.key === "-" || e.key === "e") e.preventDefault();
                                                        }}
                                                    />
                                                    <span className="input-group-text">hr</span>
                                                </div>
                                            ) : (
                                                <>
                                                    {vendor.VendorEntryPolicy?.late_arrival_hour ?? "N/A"} hr
                                                </>
                                            )}
                                        </td>

                                        <td className="text-center p-2" style={{ minWidth: "150px" }}>
                                            {editVendorId === vendor.id ? (
                                                <div className="d-flex justify-content-center">
                                                    <button
                                                        className="btn me-2"
                                                        style={{
                                                            backgroundColor: "#BFBFBF",
                                                            color: "#000",
                                                            border: "none",
                                                            padding: "4px 8px",
                                                            borderRadius: "4px",
                                                            fontSize: "12px"
                                                        }}
                                                        onClick={() => setEditVendorId(null)}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        className="btn"
                                                        style={{
                                                            backgroundColor: "rgb(10, 69, 149)",
                                                            color: "white",
                                                            border: "none",
                                                            padding: "4px 8px",
                                                            borderRadius: "4px",
                                                            fontSize: "12px"
                                                        }}
                                                        onClick={() => handleUpdateVendor(vendor.id)}
                                                    >
                                                        Save
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    <img src={View} alt="View" onClick={() => handleView(vendor.id)}
                                                        className="action-icon mx-2" style={{ width: "18px", height: "18px", cursor: "pointer" }} />
                                                    <img src={Edit} alt="Edit" onClick={() => handleEdit(vendor)}
                                                        className="action-icon mx-2" style={{ width: "18px", height: "18px", cursor: "pointer" }} />
                                                    <img src={Delete} alt="Delete"
                                                        className="action-icon mx-2" style={{ width: "18px", height: "18px", cursor: "pointer" }} />
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center p-3">No vendors available</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    <div className="pagination-controls d-flex justify-content-center align-items-center mt-3">
                        {/* Previous Button */}
                        <button
                            className='btn btn-sm btn-primary mx-2'
                            onClick={() => handlePageChange(page - 1)}
                            style={{ backgroundColor: "#0A4595", color: "white" }}
                            disabled={page <= 1}
                        >
                            <i className="fa fa-arrow-left"></i> Previous
                        </button>

                        {/* Page Input */}
                        <div className="d-flex align-items-center mx-2">
                            <span className="mx-1">Page</span>
                            <input
                                type="number"
                                style={{ width: "60px", textAlign: "center" }}
                                value={pageInput}
                                onChange={handlePageInputChange}
                                onBlur={handlePageInputBlur}
                                onKeyDown={handlePageInputKeyDown}
                                min={1}
                                max={totalPages || 1}
                            />

                            {/* Total Pages */}
                            <span className="mx-2">of {totalPages || 1}</span>
                        </div>

                        {/* Next Button */}
                        <button
                            className='btn btn-sm btn-primary mx-2'
                            onClick={() => handlePageChange(page + 1)}
                            style={{ backgroundColor: "#0A4595", color: "white" }}
                            disabled={page >= totalPages}
                        >
                            Next <i className="fa fa-arrow-right"></i>
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default Vendor;